import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["color", "compact", "rotate", "rotateLock", "tabIndex", "verticalAlign", "size", "icon", "padding", "borderStyle", "borderWidth", "title", "onClick", "onMouseLeave", "onMouseEnter", "onFocus", "onBlur", "onKeyPress", "role", "id", "aria-label", "aria-labelledby"];
import styled from 'foundations-theming/utils';
import { useIconSize } from 'foundations-assets/icons/useIconSize';
import { forwardRef } from 'react';
import { filterProps } from '../../../utils/filterProps';
import { useMapTokenPropsToCSS } from '../../../hooks/useMapTokenPropsToCSS';
import { useIconCircleContext } from '../IconCircle/IconCircleProvider';
import { useTabContext } from '../Tabs/TabProvider';
import { deepMerge } from '../../utils/deepMerge';
import { useColorSwatchContext } from '../ColorSwatch/ColorSwatchProvider';
import { useLinkContext } from '../Link/LinkProvider';
import { useIconButtonContext } from '../IconButton/IconButtonProvider';
import { useTooltipContext } from '../Tooltip/TooltipProvider';
import { jsx as _jsx } from "react/jsx-runtime";
export const StyledIconHolder = styled.span.withConfig({
  displayName: "IconHolder__StyledIconHolder"
})(({
  $compact,
  $rotate,
  $isIconCircle,
  $isLegacyIconCircle,
  $verticalAlign,
  $size,
  $cssStyles,
  $styleOverrides
}) => {
  return deepMerge(Object.assign({
    display: 'inline-flex',
    fontSize: $size != null ? useIconSize($size) : undefined,
    lineHeight: '1',
    // The unitless value isn't tokenized because it's relative to the tokenized font size
    transform: $rotate != null ? `rotate(${$rotate}deg)` : undefined,
    userSelect: 'none',
    position: 'relative',
    verticalAlign: $verticalAlign
  }, !$compact ? {
    paddingBlock: $isIconCircle ? '0.75em' : '0.5em',
    paddingInline: $isIconCircle ? '0.75em' : '0.5em'
  } : undefined, $cssStyles, $isLegacyIconCircle ? {
    display: 'inline-flex',
    alignItems: 'center',
    blockSize: '2em',
    // The em value isn't tokenized because it's relative to the tokenized font size
    justifyContent: 'center',
    inlineSize: '2em' // The em value isn't tokenized because it's relative to the tokenized font size
  } : undefined), $styleOverrides);
});
export const StyledIconWrapper = styled.span.withConfig({
  displayName: "IconHolder__StyledIconWrapper"
})(({
  $compact,
  $rotate,
  $rotateLock
}) => {
  return Object.assign({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    blockSize: '1em',
    // The em value isn't tokenized because it's relative to the tokenized font size
    inlineSize: '1em'
  }, !$compact ? {
    margin: '0.125em'
  } : undefined, {
    // Allows us to keep the same size as `UIIconHolder` and also keep the icons centered
    transform: $rotate != null && !$rotateLock ? `rotate(${$rotate * -1}deg)` : undefined
  });
});
export const IconHolder = /*#__PURE__*/forwardRef((_ref, ref) => {
  var _iconCircleContext$is;
  let {
      color,
      compact = false,
      rotate = 0,
      rotateLock,
      tabIndex,
      verticalAlign = '-0.1em',
      // Aligns the icon with text by default
      size,
      icon: Icon,
      padding,
      borderStyle = 'solid',
      borderWidth = '0',
      title,
      onClick,
      onMouseLeave,
      onMouseEnter,
      onFocus,
      onBlur,
      onKeyPress,
      role,
      id,
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    tokenProps,
    domProps
  } = filterProps(Object.assign({
    padding,
    borderStyle,
    borderWidth
  }, rest));
  const cssStyles = useMapTokenPropsToCSS(tokenProps);
  const iconCircleContext = useIconCircleContext();
  const colorSwatchContext = useColorSwatchContext();
  const tabContext = useTabContext();
  const linkContext = useLinkContext();
  const tooltipContext = useTooltipContext();
  const iconButtonContext = useIconButtonContext();
  const styleOverrides = deepMerge(colorSwatchContext === null || colorSwatchContext === void 0 ? void 0 : colorSwatchContext.iconCircleStyleOverrides, tabContext === null || tabContext === void 0 ? void 0 : tabContext.tabIconStyleOverrides, linkContext === null || linkContext === void 0 ? void 0 : linkContext.iconStyleOverrides, tooltipContext === null || tooltipContext === void 0 ? void 0 : tooltipContext.iconStyleOverrides, iconButtonContext === null || iconButtonContext === void 0 ? void 0 : iconButtonContext.iconStyleOverrides);
  return /*#__PURE__*/_jsx(StyledIconHolder, Object.assign({
    ref: ref,
    $compact: compact,
    $rotate: rotate,
    $isIconCircle: !!iconCircleContext,
    $isLegacyIconCircle: (_iconCircleContext$is = iconCircleContext === null || iconCircleContext === void 0 ? void 0 : iconCircleContext.isLegacy) !== null && _iconCircleContext$is !== void 0 ? _iconCircleContext$is : false,
    $verticalAlign: verticalAlign,
    $size: size,
    $cssStyles: cssStyles,
    $styleOverrides: styleOverrides,
    tabIndex: tabIndex,
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledBy,
    title: title,
    onClick: onClick,
    onMouseLeave: onMouseLeave,
    onMouseEnter: onMouseEnter,
    onFocus: onFocus,
    onBlur: onBlur,
    onKeyPress: onKeyPress,
    role: role,
    id: id
  }, domProps, {
    children: /*#__PURE__*/_jsx(StyledIconWrapper, {
      $compact: compact,
      $rotate: rotate,
      $rotateLock: rotateLock,
      children: /*#__PURE__*/_jsx(Icon, {
        color: color,
        size: size
      })
    })
  }));
});
IconHolder.displayName = 'IconHolder';